import {
  FlexBox,
  Grid,
  space,
  StatusCard,
} from "@waldner-laboreinrichtungen/component-system";
import type { FC } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styledComponents from "styled-components";

import type { LightStatus } from "../api/laboperator.types";
import type { EcoStats } from "../hooks/use-eco";
import {
  getIncidentCountForDevices,
  getIncidentsForDevice,
} from "../hooks/use-incidents";
import { useUnitStore } from "../store/unit-store";
import type {
  Channel,
  Device,
  DeviceClass,
  Incident,
  OperationMode,
} from "../view-model";
import { EcoWidgetPartial } from "./eco-widget";
import { GroupControl } from "./group-control";
import { GroupControlModal } from "./group-control-modal";
import { RoomMonitor } from "./room-monitor.tsx";

const DASHBOARD_WIDTH = "1200px";

export type DashboardProps = {
  className?: string;
  devices: Device[];
  incidents: Incident[];
  monitorData: {
    error?: Error;
    isLoading: boolean;
    channels: Channel[];
  };
  ecoData: {
    error?: Error;
    isLoading: boolean;
    stats?: EcoStats;
  };
};

type DashboardCategories = Exclude<DeviceClass, "monitoring">;

type SubGroup = {
  displayName: string;
  deviceList: Device[];
  incidentCount: {
    errors: number;
    warnings: number;
  };
};

type DeviceGroupMap = {
  [key in DeviceClass]: {
    displayName: string;
    deviceList: Device[];
    subGroupMap: Record<string, SubGroup>;
    incidentCount: {
      errors: number;
      warnings: number;
    };
    variant: "OK" | "ERROR" | "WARNING";
    unhealthyCount: number;
  };
};

const StyledDashboard = styledComponents.div`
  width: ${DASHBOARD_WIDTH};
  min-width: ${DASHBOARD_WIDTH};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
`;

const useGroupedDevices = (devices: Device[], incidents: Incident[]) => {
  const { t } = useTranslation();
  // Mount-aware: an incident on a mounted FU/function carries the SOURCE path,
  // so the host's view-units must be resolved to attribute it (like the detail
  // view). Without this the room overview shows "OK" for hosted mount errors.
  const getUnits = useUnitStore((s) => s.getUnitsForDevice);

  const DASHBOARD_CATEGORIES: DashboardCategories[] = [
    "workplace",
    "storage",
    "device",
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tempDeviceGroupMap: any = {};

  for (const category of DASHBOARD_CATEGORIES) {
    tempDeviceGroupMap[category] = {
      displayName: t(`dashboard.${category}`),
      deviceList: [],
      subGroupMap: {},
      incidentCount: {
        errors: 0,
        warnings: 0,
      },
      variant: "OK",
      unhealthyCount: 0,
    };
  }

  const deviceGroupMap: DeviceGroupMap = tempDeviceGroupMap;

  if (devices) {
    for (const device of devices) {
      if (device.class && deviceGroupMap[device.class]) {
        deviceGroupMap[device.class].deviceList.push(device);

        const deviceIncidents = getIncidentsForDevice(
          device,
          incidents,
          getUnits(device.id)
        );

        deviceGroupMap[device.class].incidentCount.errors +=
          deviceIncidents.errors.length;

        deviceGroupMap[device.class].incidentCount.warnings +=
          deviceIncidents.warnings.length;

        if (
          device.state?.status === "offline" ||
          deviceIncidents.errors.length > 0 ||
          deviceIncidents.warnings.length > 0
        ) {
          deviceGroupMap[device.class].unhealthyCount += 1;
        }

        if (!(device.subclass in deviceGroupMap[device.class].subGroupMap)) {
          deviceGroupMap[device.class].subGroupMap[device.subclass] = {
            deviceList: [],
            displayName: t(`subclass.${device.subclass}`),
            incidentCount: {
              errors: 0,
              warnings: 0,
            },
          };
        }
        deviceGroupMap[device.class]?.subGroupMap[
          device.subclass
        ].deviceList.push(device);
      }
    }
  }

  for (const category of DASHBOARD_CATEGORIES) {
    for (const subGroup in deviceGroupMap[category].subGroupMap) {
      deviceGroupMap[category].subGroupMap[subGroup] = {
        ...deviceGroupMap[category].subGroupMap[subGroup],
        incidentCount: getIncidentCountForDevices(
          deviceGroupMap[category].subGroupMap[subGroup].deviceList,
          incidents,
          getUnits
        ),
      };
    }

    if (deviceGroupMap[category].incidentCount.errors) {
      deviceGroupMap[category].variant = "ERROR";
    } else if (deviceGroupMap[category].incidentCount.warnings) {
      deviceGroupMap[category].variant = "WARNING";
    }
  }

  return {
    groupedDevices: deviceGroupMap,
  };
};

export const Dashboard: FC<DashboardProps> = ({
  devices,
  ecoData,
  incidents,
  monitorData,
}) => {
  const { t } = useTranslation();
  const { groupedDevices } = useGroupedDevices(devices, incidents);
  const [openGroupModal, setOpenGroupModal] = useState(false);
  const [selectedControlMode, setSelectedControlMode] = useState<
    OperationMode | LightStatus
  >("normal");
  const [selectedControlType, setSelectedControlType] = useState<
    "light" | "operation_status"
  >("operation_status");

  const onGroupControlClick = (
    mode: OperationMode | LightStatus,
    type: "light" | "operation_status"
  ) => {
    setSelectedControlMode(mode);
    setSelectedControlType(type);
    setOpenGroupModal(true);
  };

  return (
    <FlexBox alignItems="center" height="100%" justifyContent="center">
      <StyledDashboard>
        <Grid marginBottom={space(10)} rowGap={space(6)}>
          <Grid.Column width={4}>
            <RoomMonitor
              channels={monitorData.channels}
              error={monitorData.error?.message}
              loading={monitorData.isLoading}
            />
          </Grid.Column>
          <Grid.Column width={4}>
            <GroupControl onClick={onGroupControlClick} />
          </Grid.Column>
          <Grid.Column width={4}>
            <EcoWidgetPartial {...ecoData} />
          </Grid.Column>
        </Grid>
        <Grid rowGap={space(6)}>
          {Object.entries(groupedDevices).map(([category, deviceGroup]) => (
            <Grid.Column key={category} width={4}>
              <StatusCard
                actionText="Details"
                deviceGroupList={Object.values(deviceGroup.subGroupMap)
                  .sort((a, b) => a.displayName.localeCompare(b.displayName))
                  .map((subGroup) => {
                    const offlineCount = subGroup.deviceList.filter(
                      (device) => device.state?.status === "offline"
                    ).length;
                    return {
                      name: `${subGroup.deviceList.length} ${subGroup.displayName}`,
                      infoText: [
                        (() => {
                          if (offlineCount > 0) {
                            return `${offlineCount} ${t("device.offline")}`;
                          }
                          const infoMessages = subGroup.deviceList.flatMap(
                            (device) => device.state?.messages || []
                          );
                          const infoCount = infoMessages.length;
                          if (infoCount > 0) {
                            return `${infoCount} ${t("dashboard.infos")}`;
                          }
                          const unhealthCountInSubgroup =
                            subGroup.incidentCount.errors +
                            subGroup.incidentCount.warnings;
                          if (unhealthCountInSubgroup > 0) {
                            // show no info label, only show warnings and errors
                            return "";
                          }
                          return "OK";
                        })(),
                      ].filter(Boolean) as string[],
                      ...(subGroup.incidentCount.errors && {
                        errorText: `${subGroup.incidentCount.errors} ${t(
                          "dashboard.errors"
                        )}`,
                      }),
                      ...(subGroup.incidentCount.warnings && {
                        warningText: `${subGroup.incidentCount.warnings}  ${t(
                          "dashboard.warnings"
                        )}`,
                      }),
                    };
                  })}
                emptyText={t("dashboard.no.devices") as string}
                errorActionText={`${t("dashboard.error.action")}`}
                errorHref={`/workplaces` + location.search}
                href={`/workplaces` + location.search}
                linkState={{
                  activeCategory: category,
                  hasErrors: deviceGroup.variant === "ERROR",
                }}
                max={Math.max(0, Number(deviceGroup.deviceList?.length || 0))}
                statusText={
                  !deviceGroup.deviceList || deviceGroup.deviceList.length === 0
                    ? ""
                    : t("dashboard.status.ok")
                }
                title={deviceGroup.displayName}
                value={Math.max(
                  0,
                  Number(
                    (deviceGroup.deviceList?.length || 0) -
                      (deviceGroup.unhealthyCount || 0)
                  )
                )}
                variant={
                  !deviceGroup.deviceList || deviceGroup.deviceList.length === 0
                    ? "NEUTRAL"
                    : deviceGroup.variant
                }
              />
            </Grid.Column>
          ))}
        </Grid>
        <GroupControlModal
          devices={groupedDevices["workplace"]?.deviceList || []}
          isOpenModal={openGroupModal}
          mode={selectedControlMode}
          setIsOpenModal={setOpenGroupModal}
          type={selectedControlType}
        />
      </StyledDashboard>
    </FlexBox>
  );
};
